<div class="code-applicability-dialog">
  <div class="dialog-header">
    <h2 mat-dialog-title>
      <mat-icon>auto_stories</mat-icon>
      Additional Compliance Analysis
    </h2>
  </div>

  <mat-dialog-content>
    <mat-stepper #stepper [linear]="true">
      <mat-step label="Select ICC Book" [stepControl]="bookStepControl">
        <form [formGroup]="bookStepControl">
          <div class="step-content">
            <p>Step 1: Choose which ICC book to analyze against this page.</p>

            <!-- Loading state -->
            @if (loadingBooks) {
              <div class="loading-books">
                <mat-progress-spinner diameter="40" mode="indeterminate"></mat-progress-spinner>
                <p>Loading available books...</p>
              </div>
            }

            <!-- Error state -->
            @if (bookLoadError && !loadingBooks) {
              <div class="book-error">
                <mat-icon color="warn">warning</mat-icon>
                <p>{{ bookLoadError }}</p>
                <button mat-button (click)="loadAvailableBooks()">Retry</button>
              </div>
            }

            <!-- Book selection -->
            @if (!loadingBooks && !bookLoadError && availableBooks.length > 0) {
              <div class="book-selection">
                <mat-form-field appearance="outline" class="book-select full-width">
                  <mat-label>Select ICC Book</mat-label>
                  <mat-select formControlName="selectedBookId" (selectionChange)="onBookSelected()">
                    @for (book of availableBooks; track book) {
                      <mat-option [value]="book.id">
                        <div class="book-option">
                          <div class="book-title">{{ book.title }}</div>
                          <div class="book-details">{{ book.abbreviation }} {{ book.year }}</div>
                        </div>
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
            }

            <!-- No books available -->
            @if (!loadingBooks && !bookLoadError && availableBooks.length === 0) {
              <div class="no-books">
                <mat-icon>info</mat-icon>
                <p>No ICC books are currently available.</p>
              </div>
            }

            <div class="step-actions">
              <button mat-raised-button matStepperNext [disabled]="bookStepControl.invalid" color="primary">
                Next
              </button>
              <button mat-button (click)="onCancel()">Cancel</button>
            </div>
          </div>
        </form>
      </mat-step>

      <mat-step label="Select Chapters & Depth">
        <div class="step-content">
          <p>Step 2: Choose which chapters to analyze and the section depth range.</p>

          <!-- Chapter Selection Mode -->
          <div class="chapter-selection-section">
            <h4>Chapter Selection</h4>

            <!-- Selection mode toggle -->
            <mat-form-field appearance="outline" class="selection-mode-field full-width">
              <mat-label>Chapter selection method</mat-label>
              <mat-select [(value)]="chapterSelectionMode" (selectionChange)="onSelectionModeChange()">
                <mat-option value="ai">Let AI select most relevant chapters</mat-option>
                <mat-option value="manual">I'll choose specific chapters</mat-option>
              </mat-select>
            </mat-form-field>

            <!-- AI Selection Mode -->
            @if (chapterSelectionMode === 'ai') {
              <div class="ai-selection">
                <mat-form-field appearance="outline" class="max-chapters-field full-width">
                  <mat-label>Number of chapters to analyze</mat-label>
                  <mat-select [(value)]="maxChapters">
                    <mat-option [value]="3">Top 3 most relevant</mat-option>
                    <mat-option [value]="5">Top 5 most relevant</mat-option>
                    <mat-option [value]="10">Top 10 most relevant</mat-option>
                    <mat-option [value]="0">All chapters</mat-option>
                  </mat-select>
                  <mat-hint>AI will analyze your page content and select the most relevant chapters</mat-hint>
                </mat-form-field>
                <div class="ai-selection-info">
                  <mat-icon>auto_awesome</mat-icon>
                  <p>The system will automatically identify the most relevant chapters based on your page content.</p>
                </div>
              </div>
            }

            <!-- Manual Selection Mode -->
            @if (chapterSelectionMode === 'manual') {
              <div class="manual-selection">
                <!-- Loading chapters -->
                @if (loadingChapters) {
                  <div class="loading-chapters">
                    <mat-progress-spinner diameter="30" mode="indeterminate"></mat-progress-spinner>
                    <p>Loading chapters...</p>
                  </div>
                }
                <!-- Chapter error -->
                @if (chapterLoadError && !loadingChapters) {
                  <div class="chapter-error">
                    <mat-icon color="warn">warning</mat-icon>
                    <p>{{ chapterLoadError }}</p>
                    <button mat-button (click)="loadChapters()">Retry</button>
                  </div>
                }
                <!-- Chapter selection -->
                @if (!loadingChapters && !chapterLoadError && availableChapters.length > 0) {
                  <div class="chapter-picker">
                    <p><strong>Select chapters to analyze:</strong></p>
                    <mat-form-field appearance="outline" class="chapter-multiselect full-width">
                      <mat-label>Choose chapters ({{ selectedChapterIds.length }} selected)</mat-label>
                      <mat-select [(value)]="selectedChapterIds" multiple>
                        @for (chapter of availableChapters; track chapter) {
                          <mat-option [value]="chapter.contentId">
                            {{ chapter.title }}
                            @if (isChapterAnalyzed(chapter.contentId)) {
                              <span class="analyzed-badge">(Analyzed)</span>
                            }
                          </mat-option>
                        }
                      </mat-select>
                      <mat-hint>Select one or more chapters for targeted analysis</mat-hint>
                    </mat-form-field>
                    <!-- Selected chapters preview -->
                    @if (selectedChapterIds.length > 0) {
                      <div class="selected-chapters">
                        <mat-chip-set>
                          @for (chapterId of selectedChapterIds; track chapterId) {
                            <mat-chip
                              (removed)="removeChapter(chapterId)"
                              [highlighted]="isChapterAnalyzed(chapterId)"
                              removable
                            >
                              {{ getChapterTitle(chapterId) }}
                              <mat-icon matChipRemove>cancel</mat-icon>
                            </mat-chip>
                          }
                        </mat-chip-set>
                        @if (hasOverlap()) {
                          <div class="overlap-warning">
                            <mat-icon>warning</mat-icon>
                            <span>Some selected chapters have already been analyzed. They will be re-processed.</span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
                <!-- No chapters available -->
                @if (!loadingChapters && !chapterLoadError && availableChapters.length === 0) {
                  <div class="no-chapters">
                    <mat-icon>info</mat-icon>
                    <p>No chapters available for this book.</p>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Section Depth Selection -->
          <div class="depth-selection-section">
            <h4>Section Depth Range</h4>
            <mat-form-field appearance="outline" class="depth-select full-width">
              <mat-label>Analysis depth</mat-label>
              <mat-select [(value)]="maxSectionDepth">
                @for (option of depthOptions; track option) {
                  <mat-option [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                }
              </mat-select>
              <mat-hint>Deeper analysis covers more detailed subsections but takes longer to complete</mat-hint>
            </mat-form-field>
          </div>

          <!-- Recomputation Options (Issue #1426) -->
          <div class="force-recompute-section">
            <mat-checkbox [(ngModel)]="forceRecompute" class="force-recompute-checkbox">
              Force recompute (skip cache)
            </mat-checkbox>
            <p class="force-recompute-hint">
              Bypasses existing cached reports, executes fresh AI analysis, and updates results.
            </p>
          </div>

          <div class="step-actions">
            <button mat-button matStepperPrevious>Back</button>
            <button mat-raised-button color="primary" [disabled]="!isStep2Valid()" (click)="onStartAnalysis()">
              Start Analysis
            </button>
            <button mat-button (click)="onCancel()">Cancel</button>
          </div>
        </div>
      </mat-step>
    </mat-stepper>
  </mat-dialog-content>
</div>
